/**
 * pas-hero-video.js — in-view autoplay for hero videos on the PAS product page.
 *
 * The PAS hero is the custom <pas-carousel> (horizontal scroll-snap). Its video
 * slides render `autoplay muted loop` via Horizon's `video` snippet, but native
 * autoplay isn't reliably scoped to what's actually on screen (and offscreen
 * carousel slides would keep decoding). This decoupled helper watches each hero
 * <video> with an IntersectionObserver: a video ≥ ~50% visible plays; anything
 * less is paused. Playback is forced muted + playsinline so there is never any
 * sound and iOS plays inline rather than going fullscreen.
 *
 * Self-contained. No deps, no module imports. Only calls the native <video>
 * play()/pause() API. Clones in pas-carousel.js carry no <video> (swapped for a
 * poster image), so the observer only ever drives the real slides. No hero
 * videos → no-op.
 */

(function () {
  if (window.__pasHeroVideoWired) return;
  window.__pasHeroVideoWired = true;

  var THRESHOLD = 0.5;

  // Tap-to-play fallback for blocked autoplay (e.g. iOS Low Power Mode, which
  // refuses even muted autoplay). Reveals a play badge that plays the EXISTING
  // <video> on tap — a user gesture always succeeds, and there is no clone /
  // second player, so no white flash. Self-contained; inline-styled.
  function armTapToPlay(video) {
    var host = video.closest('deferred-media') || video.parentElement;
    if (!host || host.__pasTapArmed) return;
    host.__pasTapArmed = true;

    var badge = document.createElement('button');
    badge.type = 'button';
    badge.className = 'pas-tap-to-play';
    badge.setAttribute('aria-label', 'Play video');
    badge.innerHTML =
      '<svg viewBox="0 0 20 20" width="22" height="22" aria-hidden="true" focusable="false">' +
      '<path fill="currentColor" d="M15.375 8.48516C16.5417 9.15874 16.5417 10.8427 15.375 11.5163L7.875 15.8464C6.70833 16.52 5.25 15.678 5.25 14.3308L5.25 5.67058C5.25 4.32343 6.70833 3.48146 7.875 4.15504L15.375 8.48516Z"/></svg>';
    badge.style.cssText =
      'position:absolute;inset:0;margin:auto;width:3.25rem;height:3.25rem;padding:0;' +
      'display:flex;align-items:center;justify-content:center;border:0;border-radius:50%;' +
      'background:rgba(0,0,0,.45);color:#fff;cursor:pointer;z-index:2;';

    function clear() {
      host.__pasTapArmed = false;
      video.removeEventListener('playing', clear);
      if (badge.parentNode) badge.parentNode.removeChild(badge);
    }
    badge.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      video.muted = true;
      video.playsInline = true;
      var p = video.play();
      if (p && typeof p.then === 'function') p.then(clear).catch(function () {});
    });
    // If the video manages to start on its own later, drop the badge.
    video.addEventListener('playing', clear);
    host.appendChild(badge);
  }

  // Before giving up to a tap badge, try to actually get it playing: iOS often
  // won't autoplay a `preload="metadata"` video because no data is buffered yet.
  // So kick a load() and retry play() once the data is ready (mirrors Horizon's
  // video-background-component). Only the still-paused/uninterrupted case.
  function recover(video) {
    if (!video.paused || video.__pasRecovering) return;
    if (video.readyState >= 2) {
      armTapToPlay(video);
      return;
    }
    video.__pasRecovering = true;
    video.preload = 'auto';
    try { video.load(); } catch (e) {}
    var done = false;
    var finish = function () {
      if (done) return;
      done = true;
      video.__pasRecovering = false;
      if (!video.paused) return;
      var p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () { armTapToPlay(video); });
      setTimeout(function () { if (video.paused) armTapToPlay(video); }, 700);
    };
    video.addEventListener('canplay', finish, { once: true });
    setTimeout(finish, 2000); // safety if 'canplay' never fires
  }

  function manage(video, ratio) {
    if (ratio >= THRESHOLD) {
      // Guarantee no sound + inline playback (iOS) regardless of how the
      // markup was rendered, then attempt play.
      video.muted = true;
      video.defaultMuted = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.preload = 'auto';
      var playing = video.play();
      // Autoplay rejected (e.g. Low Power Mode) → try to recover, then tap-to-play.
      if (playing && typeof playing.catch === 'function') {
        playing.catch(function () {
          recover(video);
        });
      }
      // iOS can also refuse autoplay SILENTLY (no rejection) — the frame just
      // sits frozen. If still paused shortly after, try to recover (load+retry)
      // and ultimately surface the tap-to-play badge so it's never a dead frame.
      setTimeout(function () {
        if (video.paused) recover(video);
      }, 800);
    } else {
      video.pause();
    }
  }

  // One observer per call; we (re)scan on init so editor section reloads and
  // late-rendered media both get picked up. Already-observed videos are tagged
  // so a re-init never double-observes them.
  function wire(root) {
    var scope = root || document;
    var heroes = scope.querySelectorAll('.product-pas__hero');
    if (!heroes.length) return;

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          manage(entries[i].target, entries[i].intersectionRatio);
        }
      },
      // Multiple thresholds so we get an update as a slide crosses 50%.
      { threshold: [0, THRESHOLD, 1] }
    );

    for (var h = 0; h < heroes.length; h++) {
      var videos = heroes[h].querySelectorAll('video');
      for (var v = 0; v < videos.length; v++) {
        var video = videos[v];
        if (video.__pasHeroObserved) continue;
        video.__pasHeroObserved = true;
        observer.observe(video);
      }
    }
  }

  function init() {
    wire(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-scan when the PAS section is (re)rendered.
  document.addEventListener('shopify:section:load', function (event) {
    wire(event.target || document);
  });
})();
